import React, { useEffect, useRef, useState } from 'react';
import { motion, useInView } from 'framer-motion';
import { FaAnchor, FaArrowRight, FaClock, FaCrosshairs, FaFish, FaMapMarkerAlt, FaShip, FaStar, FaTrophy, FaUsers, FaWater } from 'react-icons/fa';
import {
  captainChristosYellowtail,
  diverWsbUnderwater,
  heroBluefinDeck,
  heroUnderwaterBluefin,
  heroWsbKelp,
} from '../assets/images/hero/imageAssets';
import SEOHead from '../components/SEOHead';
import { getLocalBusinessSchema, getOgImageUrl, OG_IMAGE_DIMENSIONS } from '../utils/seoSchemas';
import {
  AboutContainer,
  AboutContent,
  AboutImage,
  AboutSection,
  AboutText,
  CaptainBadge,
  CaptainCard,
  CaptainImage,
  CaptainInfo,
  CaptainName,
  CaptainQuote,
  CaptainRole,
  CaptainSection,
  CaptainWrapper,
  CtaButton,
  CtaContainer,
  CtaSection,
  CtaSubtext,
  CtaTitle,
  HeroButtons,
  HeroContent,
  HeroSection,
  HeroSubtitle,
  HeroTag,
  HeroTitle,
  PrimaryButton,
  SecondaryButton,
  SectionEyebrow,
  SectionTitle,
  ServiceCard,
  ServiceIcon,
  ServicePrice,
  ServicesContainer,
  ServicesGrid,
  ServicesSection,
  ServiceText,
  ServiceTitle,
  SpeciesList,
  SpeciesTag,
  StatItem,
  StatLabel,
  StatNumber,
  StatsBar,
  StatsContainer,
  TestimonialAuthor,
  TestimonialAvatar,
  TestimonialCard,
  TestimonialInfo,
  TestimonialName,
  TestimonialRole,
  TestimonialRating,
  TestimonialsContainer,
  TestimonialsSection,
  TestimonialsWrapper,
  TestimonialText,
  WhyCard,
  WhyGrid,
  WhyIcon,
  WhySection,
  WhyText,
  WhyTitle,
} from './styles/HomePage.styles';

// Scroll-triggered wrapper
const AnimatedSection = ({ children, delay = 0 }: { children: React.ReactNode; delay?: number }) => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-60px" });
  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 50 }}
      animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 50 }}
      transition={{ duration: 0.7, delay, ease: "easeOut" }}
    >
      {children}
    </motion.div>
  );
};

// Animated counter
const Counter = ({ end, suffix = '', label }: { end: number; suffix?: string; label: string }) => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true });
  const [count, setCount] = useState(0);

  useEffect(() => {
    if (!isInView) return;
    const duration = 2000;
    const startTime = Date.now();
    const animate = () => {
      const elapsed = Date.now() - startTime;
      const progress = Math.min(elapsed / duration, 1);
      const eased = 1 - Math.pow(1 - progress, 3); // ease-out cubic
      setCount(Math.round(eased * end));
      if (progress < 1) requestAnimationFrame(animate);
    };
    requestAnimationFrame(animate);
  }, [isInView, end]);

  return (
    <StatItem ref={ref}>
      <StatNumber>{count}{suffix}</StatNumber>
      <StatLabel>{label}</StatLabel>
    </StatItem>
  );
};

const HomePage = () => {
  return (
    <>
      <SEOHead
        title="Fussaki Spearfishing & Sportfishing Charters — San Pedro, CA"
        description="Premium spearfishing and sportfishing charters out of San Pedro, CA. Catalina Island, San Clemente Island, open blue water. White Seabass, Yellowtail, Bluefin Tuna. Book with Captain Christos."
        canonical="https://fussaki.com"
        ogImage={getOgImageUrl(heroUnderwaterBluefin)}
        ogImageWidth={OG_IMAGE_DIMENSIONS.heroUnderwaterBluefin.width}
        ogImageHeight={OG_IMAGE_DIMENSIONS.heroUnderwaterBluefin.height}
        jsonLd={getLocalBusinessSchema()}
      />

      {/* ─── Hero ─── */}
      <HeroSection $bgImage={heroUnderwaterBluefin} role="img" aria-label="Spearfisher diving with Bluefin Tuna underwater in Southern California">
        <HeroContent>
          <HeroTag>San Pedro, California</HeroTag>
          <HeroTitle>
            Dive Deep.<br /><span>Hunt Big.</span>
          </HeroTitle>
          <HeroSubtitle>
            Premium spearfishing and sportfishing charters out of San Pedro.
            Captain-led trips targeting White Seabass, Yellowtail, and Bluefin Tuna
            across Southern California's best waters.
          </HeroSubtitle>
          <HeroButtons>
            <PrimaryButton to="/book">
              Book Your Trip {FaArrowRight({ size: 14 })}
            </PrimaryButton>
            <SecondaryButton to="/charters">
              View Charters
            </SecondaryButton>
          </HeroButtons>
        </HeroContent>
      </HeroSection>

      {/* ─── Stats Bar ─── */}
      <StatsBar>
        <StatsContainer>
          <Counter end={500} suffix="+" label="Dives Completed" />
          <Counter end={12} suffix="" label="Years Experience" />
          <Counter end={6} suffix="" label="Max Divers Per Trip" />
          <Counter end={98} suffix="%" label="Client Return Rate" />
        </StatsContainer>
      </StatsBar>

      {/* ─── About ─── */}
      <AboutSection>
        <AboutContainer>
          <AnimatedSection>
            <AboutImage>
              <img
                src={diverWsbUnderwater}
                alt="Spearfisher with White Seabass underwater in Southern California"
              />
            </AboutImage>
          </AnimatedSection>
          <AnimatedSection delay={0.2}>
            <AboutContent>
              <SectionEyebrow>Who We Are</SectionEyebrow>
              <SectionTitle>Spearfishing & Sportfishing. It's All We Do.</SectionTitle>
              <AboutText>
                Fussaki isn't a charter company that added spearfishing to the menu. It's a
                spearfishing and sportfishing operation built from the ground up by Captain Christos —
                a lifelong waterman with deep knowledge of Southern California's fishing grounds.
              </AboutText>
              <AboutText>
                We run out of San Pedro aboard the WorldCat 295CC, accessing some of the most
                productive waters on the West Coast: Catalina Island, the Horseshoe Kelp, Palos Verdes
                reefs, offshore kelp paddies, and oil rig structure where trophy fish stack up.
              </AboutText>
              <SpeciesList>
                <SpeciesTag>White Seabass</SpeciesTag>
                <SpeciesTag>Yellowtail</SpeciesTag>
                <SpeciesTag>Bluefin Tuna</SpeciesTag>
                <SpeciesTag>Calico Bass</SpeciesTag>
                <SpeciesTag>Halibut</SpeciesTag>
                <SpeciesTag>Dorado</SpeciesTag>
                <SpeciesTag>Sheephead</SpeciesTag>
              </SpeciesList>
              <PrimaryButton to="/about">
                Our Story {FaArrowRight({ size: 14 })}
              </PrimaryButton>
            </AboutContent>
          </AnimatedSection>
        </AboutContainer>
      </AboutSection>

      {/* ─── Why Fussaki ─── */}
      <WhySection>
        <AnimatedSection>
          <SectionEyebrow style={{ textAlign: 'center' }}>Why Fussaki</SectionEyebrow>
          <SectionTitle style={{ textAlign: 'center' }}>
            No Fluff. No Filler. Just Results.
          </SectionTitle>
        </AnimatedSection>
        <WhyGrid>
          {[
            {
              icon: FaCrosshairs({ size: 28 }),
              title: 'Expert Local Knowledge',
              text: 'Captain Christos reads the conditions — swell, current, visibility, water temp — and makes the call on where to hunt. No following the fleet. Every trip is dialed in to what\'s running.',
            },
            {
              icon: FaUsers({ size: 28 }),
              title: 'Small Groups Only',
              text: 'Max 6 per trip. No crowds, no fighting for position. Everyone gets proper time in the water and personal attention from the captain and crew.',
            },
            {
              icon: FaShip({ size: 28 }),
              title: 'Purpose-Built Vessel',
              text: 'The WorldCat 295CC is set up specifically for spearfishing and sportfishing — easy water entry, dedicated gear racks, insulated fish holds, and the range to reach offshore waters.',
            },
            {
              icon: FaFish({ size: 28 }),
              title: 'We Put You on Fish',
              text: 'White Seabass in the kelp, Yellowtail on the iron, Bluefin in the open blue. Our track record speaks for itself — check the photos.',
            },
          ].map((item, i) => (
            <AnimatedSection key={i} delay={i * 0.12}>
              <WhyCard>
                <WhyIcon>{item.icon}</WhyIcon>
                <WhyTitle>{item.title}</WhyTitle>
                <WhyText>{item.text}</WhyText>
              </WhyCard>
            </AnimatedSection>
          ))}
        </WhyGrid>
      </WhySection>

      {/* ─── Charters ─── */}
      <ServicesSection $bgImage={heroWsbKelp} role="img" aria-label="White Seabass swimming through kelp forest">
        <ServicesContainer>
          <AnimatedSection>
            <SectionEyebrow style={{ textAlign: 'center' }}>Our Charters</SectionEyebrow>
            <SectionTitle style={{ textAlign: 'center' }}>
              Pick Your Mission
            </SectionTitle>
          </AnimatedSection>

          <ServicesGrid>
            {[
              {
                icon: FaWater({ size: 32 }),
                title: 'Half-Day Spearfishing',
                price: 'From $1,799 / $300 per person',
                text: '4 hours targeting nearshore kelp and reef structure. White Seabass, Calico Bass, Halibut. Gear, ice, and captain guidance included.',
                delay: 0,
              },
              {
                icon: FaShip({ size: 32 }),
                title: 'Full-Day Expedition',
                text: '8 hours offshore — oil rigs, islands, and deep structure. Yellowtail, Bluefin Tuna, and whatever else is running. Maximum bottom time.',
                price: 'From $2,999 / $499 per person',
                delay: 0.12,
              },
              {
                icon: FaTrophy({ size: 32 }),
                title: 'Sportfishing Charter',
                text: 'Rod-and-reel action targeting Bluefin Tuna, Yellowtail, Dorado, and more. Captain Christos puts you on the bite with expert local knowledge.',
                price: 'From $3,499 / private charter pricing',
                delay: 0.24,
              },
              {
                icon: FaAnchor({ size: 32 }),
                title: 'Private Charter',
                text: 'Book the whole boat for your crew. Custom itinerary, flexible schedule, your call on targets. Spearfishing, sportfishing, or both — your trip, your way.',
                price: 'Contact for Pricing',
                delay: 0.36,
              },
            ].map((svc, i) => (
              <AnimatedSection key={i} delay={svc.delay}>
                <ServiceCard>
                  <ServiceIcon>{svc.icon}</ServiceIcon>
                  <ServiceTitle>{svc.title}</ServiceTitle>
                  <ServiceText>{svc.text}</ServiceText>
                  <ServicePrice>{svc.price}</ServicePrice>
                </ServiceCard>
              </AnimatedSection>
            ))}
          </ServicesGrid>
        </ServicesContainer>
      </ServicesSection>

      {/* ─── Captain ─── */}
      <CaptainSection>
        <CaptainWrapper>
          <AnimatedSection>
            <CaptainCard>
              <CaptainImage>
                <img
                  src={captainChristosYellowtail}
                  alt="Captain Christos with a haul of Yellowtail aboard the WorldCat 295CC"
                />
              </CaptainImage>
              <CaptainInfo>
                <SectionEyebrow>Meet the Captain</SectionEyebrow>
                <CaptainName>Captain Christos</CaptainName>
                <CaptainRole>Founder & Head Guide</CaptainRole>
                <CaptainQuote>
                  "I built Fussaki around one idea — run the charter I'd want to book myself.
                  Small crew, real hunting, no shortcuts. I've spent years learning these waters,
                  tracking the fish, reading the conditions. When we leave the dock,
                  we're going where the fish are — not where everyone else is going."
                </CaptainQuote>
                <CaptainBadge>
                  {FaMapMarkerAlt({ size: 14 })} San Pedro, CA · {FaClock({ size: 14 })} 12+ Years on the Water
                </CaptainBadge>
              </CaptainInfo>
            </CaptainCard>
          </AnimatedSection>
        </CaptainWrapper>
      </CaptainSection>

      {/* ─── CTA ─── */}
      <CtaSection $bgImage={heroBluefinDeck} role="img" aria-label="Anglers aboard the WorldCat 295CC with Bluefin Tuna catch">
        <AnimatedSection>
          <CtaContainer>
            <SectionEyebrow style={{ textAlign: 'center' }}>Ready?</SectionEyebrow>
            <CtaTitle>Spots Fill Fast. The Fish Won't Wait.</CtaTitle>
            <CtaSubtext>
              Lock in your date and get on the water with Captain Christos.
              Open party spots and private charters available — check live availability now.
            </CtaSubtext>
            <CtaButton to="/book">
              Check Availability {FaArrowRight({ size: 16 })}
            </CtaButton>
          </CtaContainer>
        </AnimatedSection>
      </CtaSection>

      {/* ─── Testimonials ─── */}
      <TestimonialsSection>
        <TestimonialsContainer>
          <AnimatedSection>
            <SectionEyebrow style={{ textAlign: 'center' }}>From Our Divers</SectionEyebrow>
            <SectionTitle style={{ textAlign: 'center' }}>
              Don't Take Our Word for It
            </SectionTitle>
          </AnimatedSection>

          <TestimonialsWrapper>
            {[
              {
                text: "Best spearfishing charter I've been on — and I've tried most of them from San Pedro to San Diego. Captain Christos knew exactly where the Yellowtail were stacked. Landed my PB on the first drop. Already rebooked.",
                name: 'Mike T.',
                role: 'Newport Beach, CA',
                avatar: 'https://randomuser.me/api/portraits/men/32.jpg',
                stars: 5,
              },
              {
                text: "First time spearfishing and I wasn't sure what to expect. Christos and the crew were patient, professional, and kept me safe the whole time. By the end of the day I'd shot my first Calico and I was hooked. Legit operation.",
                name: 'Sarah L.',
                role: 'Long Beach, CA',
                avatar: 'https://randomuser.me/api/portraits/women/44.jpg',
                stars: 5,
              },
              {
                text: "Private charter for my buddy's birthday — 6 of us, full day out to Catalina. Everyone got fish. Crew handled everything, cleaned the catch, had it on ice. No tourist vibes — just a real day on the water with people who know it.",
                name: 'Robert K.',
                role: 'San Diego, CA',
                avatar: 'https://randomuser.me/api/portraits/men/67.jpg',
                stars: 5,
              },
            ].map((t, i) => (
              <AnimatedSection key={i} delay={i * 0.15}>
                <TestimonialCard>
                  <TestimonialRating>
                    {Array.from({ length: t.stars }).map((_, j) => (
                      <span key={j}>{FaStar({ size: 14 })}</span>
                    ))}
                  </TestimonialRating>
                  <TestimonialText>"{t.text}"</TestimonialText>
                  <TestimonialAuthor>
                    <TestimonialAvatar>
                      <img src={t.avatar} alt={t.name} />
                    </TestimonialAvatar>
                    <TestimonialInfo>
                      <TestimonialName>{t.name}</TestimonialName>
                      <TestimonialRole>{t.role}</TestimonialRole>
                    </TestimonialInfo>
                  </TestimonialAuthor>
                </TestimonialCard>
              </AnimatedSection>
            ))}
          </TestimonialsWrapper>
        </TestimonialsContainer>
      </TestimonialsSection>
    </>
  );
};

export default HomePage;
